import React, { useState, useEffect } from 'react';
import { Fab, Tooltip, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever
} from "@mui/icons-material";

import { 
  Feature, 
  FeatureSource, 
  FloatingFeature,
} from "@models";
import { 
  AttachedTable,
  CharacterFeatureSource, 
  CharacterSheetInterface, 
  TrueCreatureInstance, 
} from "@dndModels";
import { CHAR_FEATURE_TYPES } from "@dndModels/Constants";

import FloatingFeatureBox from "@dndComponents/FloatingFeatureBox";
import FloatingFeatureInput from "@dndComponents/feature/FloatingFeatureInput";
import { FeatureType } from "@dndModels/Types";

interface Props {
  label: string;
  parent_name: string;
  base_name?: string | null;
  feature: Feature;
  feature_source?: FeatureSource;
  onChange: (feature: Feature) => void; 
  onDelete: () => void; 
  onDone: (target: string) => void;
  slot_level?: number;
  minion?: boolean;
  disabled?: boolean;
  formHeight?: number;
  allowed_feature_types?: FeatureType[];
  cfs?: CharacterSheetInterface | CharacterFeatureSource;
  attached_table?: AttachedTable;
}

export default function FeatureInput({ 
  label,
  parent_name,
  base_name = null,
  feature,
  feature_source,
  onChange,
  onDelete,
  onDone,
  slot_level = -1,
  minion = false,
  disabled = false,
  formHeight,
  allowed_feature_types = CHAR_FEATURE_TYPES,
  cfs,
  attached_table
}: Props) {
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [my_feature, setFeature] = useState(feature.clone());
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading]);

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}>
        <Button
          variant="contained"
          color="primary"
          disabled={ my_feature.name === "" || unsavedChanges }
          onClick={ () => { 
            onDone("parent");
          }}>
          { parent_name === "" ? `Nameless ${my_feature.parent_type}` : parent_name }
        </Button>
        { base_name !== null &&
          <span>
            &gt;
            <Button
              variant="contained"
              color="primary"
              disabled={ my_feature.name === "" || unsavedChanges }
              onClick={ () => { 
                onDone("base");
              }}>
              { base_name === "" ? `Nameless Feature Base` : base_name }
            </Button>
          </span>
        }
      </Grid>
      { !disabled &&
        <>
          <Grid size={2}>
            <Tooltip title={`Delete ${label}`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  onDelete();
                }}>
                <DeleteForever/>
              </Fab>
            </Tooltip>
          </Grid>
          <Grid size={10}>
            <FloatingFeatureBox
              floating_feature_id={my_feature.floating_feature_id}
              allowed_feature_types={allowed_feature_types}
              onFloatingFeatureSelected={(floating_feature: FloatingFeature) => {
                const new_feature = my_feature;
                new_feature.floating_feature_id = floating_feature._id;
                new_feature.floating_feature = floating_feature;
                setFeature(new_feature);
                setReloading(true);
                setUnsavedChanges(false);
                onChange(new_feature);
              }}
            />
          </Grid>
        </>
      }
      { !reloading &&
        <Grid size={12}>
          <FloatingFeatureInput 
            floating_feature={my_feature.floating_feature}
            feature_source={feature_source}
            slot_level={slot_level}
            allowed_feature_types={allowed_feature_types}
            height={formHeight === undefined ? undefined : formHeight - 149}
            attached_table={attached_table}
            onAdd={(floating_feature: FloatingFeature) => {
              const new_feature = my_feature;
              new_feature.floating_feature_id = floating_feature._id;
              new_feature.floating_feature = floating_feature;
              setFeature(new_feature);
              onChange(new_feature);
            }}
            unsavedChange={setUnsavedChanges}
            cfs={cfs}
          />
        </Grid>
      }
    </Grid>
  );
}
